import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from '../hooks/useTranslation';

const { FiShield, FiHeart, FiUsers, FiCheckCircle, FiArrowRight, FiPlay, FiMessageCircle, FiHome, FiClock } = FiIcons;

function HomePage({ setShowVideo }) {
  const { t } = useTranslation();
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const handleGetQuote = () => {
    window.open('https://quote.burbankinsuranceservices.com/1b0DiB', '_blank');
  };

  const benefits = t('homepage.benefits');
  const workWithUsItems = t('homepage.workWithUsItems');
  const askItems = t('homepage.askItems');
  const perfectItems = t('homepage.perfectItems');

  const trustLogos = [
    {
      src: "https://app1.sharemyimage.com/2025/08/18/cdnlogo.com_business-insider.png",
      alt: "Business Insider",
      className: "h-8"
    },
    {
      src: "https://app1.sharemyimage.com/2025/08/18/603-6035540_yahoo-finance-logo-png-transparent-png.png",
      alt: "Yahoo Finance",
      className: "h-8"
    },
    {
      src: "https://app1.sharemyimage.com/2025/08/18/FORTUNE-LOGO-2016.png",
      alt: "Fortune",
      className: "h-8"
    },
    {
      src: "https://app1.sharemyimage.com/2025/08/18/png-clipart-logo-forbes-industry-magazine-black-cover-company-text.png",
      alt: "Forbes",
      className: "h-8"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            className="text-center mb-16"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              {t('homepage.heroTitle')} <span className="text-blue-600 block">{t('homepage.heroTitleHighlight')}</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            >
              {t('homepage.heroSubtitle')}
            </motion.p>
            
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={() => setShowVideo(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPlay} />
                <span>{t('homepage.watchVideo')}</span>
              </button>
              <button
                onClick={() => document.getElementById('chatbot').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white hover:bg-gray-50 text-blue-600 border border-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMessageCircle} />
                <span>{t('homepage.chatWithAI')}</span>
              </button>
            </motion.div>
          </motion.div>

          {/* Ethos Logo */}
          <motion.div variants={fadeInUp} className="flex flex-col items-center mb-8">
            <p className="text-sm text-gray-500 mb-4">{t('homepage.poweredBy')}</p>
            <img
              src="https://app1.sharemyimage.com/2025/08/18/ethos-logo_RGB_cypress.jpg"
              alt="Ethos Life Insurance"
              className="h-12 object-contain"
            />
          </motion.div>

          {/* Trust Indicators */}
          <motion.div variants={fadeInUp} className="flex flex-col items-center mb-16">
            <p className="text-sm text-gray-500 mb-6">{t('homepage.asFeaturedIn')}</p>
            <div className="flex flex-wrap items-center justify-center gap-8 opacity-60">
              {trustLogos.map((logo, index) => (
                <img
                  key={index}
                  src={logo.src}
                  alt={logo.alt}
                  className={`${logo.className} object-contain grayscale hover:grayscale-0 transition-all duration-300`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Chatbot Section */}
      <section id="chatbot" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="text-center mb-12"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold text-gray-900 mb-6">
              {t('homepage.expertGuidanceTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {t('homepage.expertGuidanceSubtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="flex justify-center"
          >
            <div className="w-full max-w-4xl bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-blue-600 rounded-full p-3 mr-4">
                  <SafeIcon icon={FiMessageCircle} className="text-white text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{t('homepage.virtualAgentTitle')}</h3>
                  <p className="text-gray-600">{t('homepage.virtualAgentSubtitle')}</p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <iframe
                  src="https://studio.pickaxe.co/_embed/Business_Insurance_Specialist__Chat_Agent_O5EHE?d=deployment-5d294cce-f428-43f7-8630-d06a450d2982"
                  style={{ width: '100%', height: '500px', border: 'none' }}
                  title="Insurance Virtual Agent"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-2" />
                    {t('homepage.whatYouCanAsk')}
                  </h4>
                  <ul className="space-y-2 text-gray-600">
                    {askItems.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiHeart} className="text-red-500 mr-2" />
                    {t('homepage.perfectIfYoure')}
                  </h4>
                  <ul className="space-y-2 text-gray-600">
                    {perfectItems.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <button
                  onClick={handleGetQuote}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
                >
                  <span>{t('homepage.readyGetPrice')}</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold text-white mb-6">
              {t('homepage.whyChooseEthosTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-blue-100 max-w-3xl mx-auto">
              {t('homepage.whyChooseEthosSubtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300"
              >
                <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={index === 0 ? FiCheckCircle : index === 1 ? FiHome : index === 2 ? FiClock : FiCheckCircle} className="text-blue-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                <p className="text-blue-100">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-16"
          >
            <img
              src="https://app1.sharemyimage.com/2025/08/18/ethos-logo_RGB_cypress.jpg"
              alt="Ethos Life Insurance"
              className="h-16 mx-auto mb-8 bg-white rounded-lg px-6 py-2"
            />
            <p className="text-blue-100 text-lg mb-8">{t('homepage.trustedBy')}</p>
            <button
              onClick={handleGetQuote}
              className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <span>{t('header.getMyPrice')}</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('homepage.aboutTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('homepage.aboutText1')}
              </p>
              <p className="text-lg text-gray-600 mb-8">
                {t('homepage.aboutText2')}
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <SafeIcon icon={FiUsers} className="text-blue-600 text-2xl" />
                  <span className="text-gray-700 font-medium">{t('homepage.proudlyServing')}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
                  <span className="text-gray-700 font-medium">{t('header.licenseNumber')}</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('homepage.whyWorkWithUs')}</h3>
              <div className="space-y-4">
                {workWithUsItems.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <button
                  onClick={handleGetQuote}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 w-full flex items-center justify-center space-x-2"
                >
                  <span>{t('header.getMyPrice')}</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold text-white mb-6">
              {t('homepage.finalCTATitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('homepage.finalCTASubtitle')}
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <span>{t('homepage.getMyPriceNow')}</span>
                <SafeIcon icon={FiArrowRight} />
              </button>
              <button
                onClick={() => setShowVideo(true)}
                className="bg-transparent hover:bg-white/10 text-white border border-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPlay} />
                <span>{t('homepage.watchVideoGuide')}</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;